import { Link, useLocation } from "react-router-dom";
import { Users, Calendar, DollarSign, Settings, LayoutDashboard, Menu, Package, Stethoscope } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useActiveOrganization } from "@/hooks/useActiveOrganization";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useEffect, useMemo, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import logo from "@/assets/logo.png";
import { useIsMobile } from "@/hooks/use-mobile";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ProfileMenu } from "@/components/profile/ProfileMenu";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Sidebar,
  SidebarProvider,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";
import { ModulePermissions } from "@/types/permissions";

interface LayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permissionKey?: { module: keyof ModulePermissions; action: string };
}

const navigation: NavigationItem[] = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, permissionKey: { module: 'dashboard', action: 'view' } },
  { name: "Pacientes", href: "/patients", icon: Users, permissionKey: { module: 'patients', action: 'view' } },
  { name: "Agenda", href: "/schedule", icon: Calendar, permissionKey: { module: 'schedule', action: 'view' } },
  { name: "Atendimento", href: "/my-schedule", icon: Stethoscope, permissionKey: { module: 'attendance', action: 'view' } },
  { name: "Financeiro", href: "/finance", icon: DollarSign, permissionKey: { module: 'finance', action: 'view' } },
  { name: "Estoque", href: "/stock", icon: Package, permissionKey: { module: 'stock', action: 'view' } },
  { name: "Configurações", href: "/settings", icon: Settings },
];

function AppSidebar() {
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { organizations } = useOrganizations();
  const { activeOrganizationId, setActiveOrganization } = useActiveOrganization();
  const { role } = useUserRole();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { hasPermission, isLoading: isLoadingPermissions } = useMemberPermissions();

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Filter navigation based on permissions
  const filteredNavigation = navigation.filter(item => {
    if (item.permissionKey) {
      return hasPermission(item.permissionKey.module, item.permissionKey.action);
    }
    return true; // Items without permissionKey (like Settings) are always visible
  });

  const activeOrganization = useMemo(
    () => organizations.find((org) => org.id === activeOrganizationId),
    [organizations, activeOrganizationId]
  );

  useEffect(() => {
    if (organizations.length > 0) {
      const orgExists = organizations.some((org) => org.id === activeOrganizationId);
      if (!activeOrganizationId || !orgExists) {
        setActiveOrganization(organizations[0].id);
      }
    } else if (activeOrganizationId) {
      setActiveOrganization(null);
    }
  }, [organizations, activeOrganizationId, setActiveOrganization]);

  const getRoleLabel = () => {
    if (role === "admin") return "Admin";
    if (role === "doctor") return "Médico";
    if (role === "secretary") return "Secretária";
    return "Usuário";
  };

  const getRoleBadgeColor = () => {
    if (role === "admin") return "bg-purple-500/20 text-purple-300 border-purple-400/30";
    if (role === "doctor") return "bg-blue-500/20 text-blue-300 border-blue-400/30";
    if (role === "secretary") return "bg-green-500/20 text-green-300 border-green-400/30";
    return "bg-gray-500/20 text-gray-300 border-gray-400/30";
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email?.charAt(0).toUpperCase() || "U";
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border/50">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="Logo"
            className={cn(
              "object-contain flex-shrink-0 transition-all",
              isCollapsed ? "h-10 w-10" : "h-10 w-10"
            )}
          />
          {!isCollapsed && (
            <>
              {organizations.length > 0 ? (
                <div className="flex-1 min-w-0">
                  <Select value={activeOrganizationId || ""} onValueChange={setActiveOrganization}>
                    <SelectTrigger
                      className={cn(
                        "w-full border-sidebar-border/30 bg-white/10 text-sidebar-foreground",
                        !activeOrganizationId && "border-orange-500"
                      )}
                    >
                      <SelectValue placeholder="Selecione uma empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!activeOrganizationId && (
                    <p className="text-xs text-orange-500 mt-1">⚠️ Selecione uma empresa</p>
                  )}
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg font-bold text-sidebar-foreground truncate">Orion</h1>
                  <p className="text-xs text-sidebar-foreground/60">Gestão Clínica</p>
                </div>
              )}
            </>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <SidebarMenu>
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  tooltip={item.name}
                  className={cn(
                    "transition-all duration-200",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
                      : "text-sidebar-foreground/70 hover:bg-white/10 hover:text-sidebar-foreground"
                  )}
                >
                  <Link to={item.href}>
                    <item.icon className="h-5 w-5" />
                    <span>{item.name}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="p-3 border-t border-sidebar-border/30">
        {isCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={signOut}
                className="flex items-center justify-center w-full"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
                    {getInitials(profile?.full_name || undefined, user?.email)}
                  </AvatarFallback>
                </Avatar>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{profile?.full_name || user?.email}</p>
              <p className="text-xs text-muted-foreground">{getRoleLabel()}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <ProfileMenu
            userEmail={user?.email}
            userName={profile?.full_name || undefined}
            avatarUrl={profile?.avatar_url || undefined}
            roleLabel={getRoleLabel()}
            roleBadgeColor={getRoleBadgeColor()}
            onSignOut={signOut}
          />
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

function MobileHeader() {
  const { signOut, user } = useAuth();
  const { organizations } = useOrganizations();
  const { activeOrganizationId, setActiveOrganization } = useActiveOrganization();
  const { role } = useUserRole();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { hasPermission } = useMemberPermissions();

  const { data: profile } = useQuery({
    queryKey: ["profile-mobile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });
  
  // Filter navigation based on permissions
  const filteredNavigation = navigation.filter(item => {
    if (item.permissionKey) {
      return hasPermission(item.permissionKey.module, item.permissionKey.action);
    }
    return true;
  });

  const activeOrganization = useMemo(
    () => organizations.find((org) => org.id === activeOrganizationId),
    [organizations, activeOrganizationId]
  );

  const getRoleLabel = () => {
    if (role === "admin") return "Admin";
    if (role === "doctor") return "Médico";
    if (role === "secretary") return "Secretária";
    return "Usuário";
  };

  const getRoleBadgeColor = () => {
    if (role === "admin") return "bg-purple-500/20 text-purple-300 border-purple-400/30";
    if (role === "doctor") return "bg-blue-500/20 text-blue-300 border-blue-400/30";
    if (role === "secretary") return "bg-green-500/20 text-green-300 border-green-400/30";
    return "bg-gray-500/20 text-gray-300 border-gray-400/30";
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-sidebar border-b border-sidebar-border/40 flex items-center justify-between px-4">
      <div className="flex items-center">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-sidebar-foreground">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 bg-sidebar [&>button]:text-sidebar-foreground">
            <div className="p-6 pb-4">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
                {organizations.length > 0 ? (
                  <div className="flex-1 min-w-0">
                    <Select value={activeOrganizationId || ""} onValueChange={setActiveOrganization}>
                      <SelectTrigger
                        className={cn(
                          "w-full border-sidebar-border/30 bg-white/10 text-sidebar-foreground",
                          !activeOrganizationId && "border-orange-500"
                        )}
                      >
                        <SelectValue placeholder="Selecione uma empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg font-bold text-sidebar-foreground truncate">Orion</h1>
                    <p className="text-xs text-sidebar-foreground/60">Gestão Clínica</p>
                  </div>
                )}
              </div>
            </div>

            <nav className="flex-1 px-3 space-y-1">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-sidebar-accent text-sidebar-accent-foreground shadow-sm"
                        : "text-sidebar-foreground/70 hover:bg-white/10 hover:text-sidebar-foreground"
                    )}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="p-3 border-t border-sidebar-border/30">
              <ProfileMenu
                userEmail={user?.email}
                userName={profile?.full_name || undefined}
                avatarUrl={profile?.avatar_url || undefined}
                roleLabel={getRoleLabel()}
                roleBadgeColor={getRoleBadgeColor()}
                onSignOut={signOut}
              />
            </div>
          </SheetContent>
        </Sheet>
        <h1 className="ml-4 text-lg font-semibold text-sidebar-foreground truncate">
          {activeOrganization?.name || "Orion"}
        </h1>
      </div>
      <NotificationBell />
    </header>
  );
}

export default function Layout({ children }: LayoutProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="flex min-h-screen bg-background min-w-0">
        <MobileHeader />
        <main className="flex-1 overflow-auto pt-14">{children}</main>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4">
            <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
            <div className="ml-auto">
              <NotificationBell variant="light" />
            </div>
          </header>
          {children}
        </main>
      </div>
    </SidebarProvider>
  );
}